import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { deleteUserRequest, disableUserRequest, editUserRequest, enableUserRequest, getUsuariosRequest, registerRequest } from '../services/auth/authData';
import { getEmpresasRequest } from '../services/empresas/empresasData';
import {
  Typography,
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Container,
  Paper,
  InputAdornment,
  TableContainer,
  TablePagination,
  Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { green, red } from "@mui/material/colors";
import { toast } from "react-toastify";

const CuentaModal = ({
  open,
  onClose,
  onSubmit,
  initialData = { emp_codigo: '', usu_nombre: '', usu_apellido: '', usu_rol: '', usu_email: '', usu_password: '' },
  empresas = [],
  isEdit = false,
}) => {
  const [form, setForm] = useState({
    emp_codigo: initialData?.emp_codigo || '',
    usu_nombre: initialData?.usu_nombre || '',
    usu_apellido: initialData?.usu_apellido || '',
    usu_rol: initialData?.usu_rol || '',
    usu_email: initialData?.usu_email || '',
    usu_password: initialData?.usu_password || '',
  });
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    setForm({
      emp_codigo: initialData?.emp_codigo || '',
      usu_nombre: initialData?.usu_nombre || '',
      usu_apellido: initialData?.usu_apellido || '',
      usu_rol: initialData?.usu_rol || '',
      usu_email: initialData?.usu_email || '',
      usu_password: initialData?.usu_password || '',
    });
    setEmailError('');
    setPasswordError('');
  }, [initialData, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });

    if (name === "usu_email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setEmailError(emailRegex.test(value) ? '' : 'Correo electrónico inválido');
    }
    if (name === "usu_password") {
      setPasswordError(value.length >= 6 ? '' : 'La contraseña debe tener al menos 6 caracteres');
    }
  };

  const handleSubmit = () => {
    if (emailError || passwordError) return;
    if (!form.usu_nombre || !form.usu_email || (!isEdit && !form.usu_password)) {
      toast.warning("Por favor complete los campos obligatorios.");
      return;
    }
    onSubmit(form);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#001F3F' }}>
          {isEdit ? "Editar Cuenta" : "Crear Nueva Cuenta"}
        </Typography>
      </DialogTitle>
      <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="empresa-label">Empresa</InputLabel>
              <Select
                labelId="empresa-label"
                label="Empresa"
                name="emp_codigo"
                value={form.emp_codigo}
                onChange={handleChange}
              >
                {(Array.isArray(empresas) ? empresas : []).map((empresa) => (
                  <MenuItem key={empresa.emp_codigo} value={empresa.emp_codigo}>
                    {empresa.emp_nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Nombre"
                name="usu_nombre"
                value={form.usu_nombre}
                onChange={handleChange}
                fullWidth
                variant="outlined"
              />
              <TextField
                label="Apellido"
                name="usu_apellido"
                value={form.usu_apellido}
                onChange={handleChange}
                fullWidth
                variant="outlined"
              />
            </Box>

            <FormControl fullWidth variant="outlined">
              <InputLabel id="rol-label">Rol</InputLabel>
              <Select
                labelId="rol-label"
                label="Rol"
                name="usu_rol"
                value={form.usu_rol}
                onChange={handleChange}
              >
                <MenuItem value="SuperAdmin">SuperAdmin</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Usuario">Usuario</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Correo Electrónico"
              name="usu_email"
              value={form.usu_email}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              error={!!emailError}
              helperText={emailError}
            />

            <TextField
              label={isEdit ? "Nueva Contraseña (Opcional)" : "Contraseña"}
              name="usu_password"
              type="password"
              value={form.usu_password}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              error={!!passwordError}
              helperText={passwordError}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} sx={{ color: '#666' }}>Cancelar</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!!emailError || !!passwordError}
            sx={{
              backgroundColor: '#001F3F',
              color: '#fff',
              '&:hover': { backgroundColor: '#003366' }
            }}
          >
            {isEdit ? "Guardar Cambios" : "Crear Cuenta"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export const GestionCuentas = () => {
  const [cuentas, setCuentas] = useState([]);
  const [filteredCuentas, setFilteredCuentas] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editCuenta, setEditCuenta] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const fetchCuentas = async () => {
    try {
      const usuarios = await getUsuariosRequest();
      const data = Array.isArray(usuarios) ? usuarios : [];
      setCuentas(data);
      setFilteredCuentas(data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Error al cargar usuarios.");
      setCuentas([]);
      setFilteredCuentas([]);
    }
  };

  useEffect(() => {
    const fetchEmpresasAsync = async () => {
      try {
        const empresasData = await getEmpresasRequest();
        setEmpresas(Array.isArray(empresasData) ? empresasData : []);
      } catch (error) {
        console.log('Error fetching empresas:', error.response?.data);
      }
    };
    fetchEmpresasAsync();
    fetchCuentas();
  }, []);

  useEffect(() => {
    const results = cuentas.filter(cuenta =>
      cuenta.usu_nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cuenta.usu_apellido.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cuenta.usu_email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCuentas(results);
    setPage(0);
  }, [searchTerm, cuentas]);

  const handleCreate = () => {
    setEditCuenta(null);
    setModalOpen(true);
  };

  const handleEdit = (cuenta) => {
    setEditCuenta({ ...cuenta, usu_password: '' }); // Clear password for security/UX
    setModalOpen(true);
  };

  const handleModalSubmit = async (data) => {
    try {
      if (editCuenta) {
        await editUserRequest(editCuenta.usu_codigo, data);
        toast.success("Cuenta actualizada exitosamente.");
      } else {
        await registerRequest(data);
        toast.success("Cuenta creada exitosamente.");
      }
      setModalOpen(false);
      fetchCuentas();
    } catch (error) {
      console.error("Error saving user:", error);
      toast.error("Error al guardar la cuenta.");
    }
  };

  const handleToggleHabilitado = async (user) => {
    try {
      if (user.usu_status) {
        await disableUserRequest(user.usu_codigo);
        toast.success("Usuario deshabilitado.");
      } else {
        await enableUserRequest(user.usu_codigo);
        toast.success("Usuario habilitado.");
      }
      fetchCuentas();
    } catch (error) {
      toast.error("Error al cambiar estado.");
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteUserRequest(deleteId);
      toast.success("Usuario eliminado correctamente.");
      setConfirmOpen(false);
      fetchCuentas();
    } catch (error) {
      toast.error("Error al eliminar el usuario.");
      setConfirmOpen(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" fontWeight="600" sx={{ color: '#001F3F' }}>
          Gestión de Cuentas
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={{
            backgroundColor: '#001F3F',
            color: '#fff',
            '&:hover': { backgroundColor: '#003366' },
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          Nueva Cuenta
        </Button>
      </Box>

      <Paper sx={{ width: '100%', mb: 2, border: '1px solid #e0e0e0' }}>
        {/* Toolbar */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e0e0e0', flexWrap: 'wrap', gap: 2 }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Buscar usuario..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', sm: 300 } }}
          />
          <Typography variant="body2" sx={{ color: '#666' }}>
            {filteredCuentas.length} {filteredCuentas.length === 1 ? 'resultado' : 'resultados'}
          </Typography>
        </Box>

        <TableContainer>
          <Table sx={{ minWidth: 750 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Empresa</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Nombre Completo</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Rol</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Correo</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#001F3F' }} align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCuentas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" sx={{ color: '#666' }}>
                      No se encontraron cuentas
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredCuentas
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((cuenta) => (
                    <TableRow
                      key={cuenta.usu_codigo}
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#001F3F' }}>
                          {empresas.find(e => e.emp_codigo === cuenta.emp_codigo)?.emp_nombre || "Sin empresa"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="500" sx={{ color: '#001F3F' }}>
                          {cuenta.usu_nombre} {cuenta.usu_apellido}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={cuenta.usu_rol}
                          size="small"
                          sx={{
                            backgroundColor: '#f5f5f5',
                            color: '#666',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          {cuenta.usu_email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={cuenta.usu_status ? "Activo" : "Inactivo"}
                          size="small"
                          sx={{
                            backgroundColor: cuenta.usu_status ? '#e8f5e9' : '#f5f5f5',
                            color: cuenta.usu_status ? '#2e7d32' : '#666',
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <IconButton
                            onClick={() => handleEdit(cuenta)}
                            size="small"
                            sx={{ color: "#666" }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => {
                              setDeleteId(cuenta.usu_codigo);
                              setConfirmOpen(true);
                            }}
                            size="small"
                            sx={{ color: "#d32f2f" }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          <Switch
                            checked={!!cuenta.usu_status}
                            onChange={() => handleToggleHabilitado(cuenta)}
                            size="small"
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: green[600],
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: green[600],
                              },
                            }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredCuentas.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
        />
      </Paper>

      {/* Modal para crear/editar */}
      <CuentaModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleModalSubmit}
        initialData={editCuenta || { emp_codigo: '', usu_nombre: '', usu_apellido: '', usu_rol: '', usu_email: '', usu_password: '' }}
        empresas={empresas}
        isEdit={!!editCuenta}
      />

      {/* Confirmación de eliminación */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#001F3F' }}>
            Confirmar Eliminación
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#666' }}>
            ¿Estás seguro de que deseas eliminar esta cuenta? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={{ color: '#666' }}>Cancelar</Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            sx={{
              backgroundColor: '#d32f2f',
              color: '#fff',
              '&:hover': { backgroundColor: '#c62828' }
            }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};